
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { RaceEvent, RaceCategory } from '../types';
import { XIcon } from './icons/PlaybackIcons';
import { raceEventService } from '../services/raceService';

interface CategorySelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    race: RaceEvent;
}

const CategorySelectionModal: React.FC<CategorySelectionModalProps> = ({ isOpen, onClose, race }) => {
    const [fullRace, setFullRace] = useState<RaceEvent | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        if (isOpen && race) {
            // If the race already has categories loaded, use it immediately
            if (race.categories && race.categories.length > 0) {
                setFullRace(race);
                setIsLoading(false);
            } else {
                setIsLoading(true);
                raceEventService.getRaceEventBySlug(race.id).then(fetchedRace => {
                    if (isMounted) {
                        setFullRace(fetchedRace || race);
                        setIsLoading(false);
                    }
                }).catch(err => {
                    console.error("Failed to load full race details", err);
                    if (isMounted) setIsLoading(false);
                });
            }
        } else {
            setFullRace(null);
        }
        return () => { isMounted = false; };
    }, [isOpen, race]);

    if (!isOpen) return null;

    const categoriesToRender = fullRace?.categories || [];
    // Sort categories by start time
    const getStartTime = (cat: RaceCategory) => cat.scheduledStartTime || cat.startTime || 0;
    const sortedCategories = [...categoriesToRender].sort((a, b) => getStartTime(a) - getStartTime(b));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[2000] p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-md border border-gray-700 relative flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-900/50 flex-shrink-0">
                    <h2 className="text-lg font-bold text-white truncate pr-4">{race.name}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto">
                    <p className="text-sm text-gray-400 mb-4">Select a category to view:</p>
                
                {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <svg className="animate-spin h-8 w-8 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                ) : categoriesToRender.length === 0 ? (
                    <p className="text-gray-500 italic text-center py-4">No categories found for this event.</p>
                ) : (
                    <div className="space-y-2">
                        {sortedCategories.map(cat => {
                            const startTime = getStartTime(cat);
                            return (
                            <Link
                                key={cat.id}
                                to={`/race/${race.slug || race.id}/category/${cat.slug || cat.id}`}
                                className="block bg-gray-700 hover:bg-cyan-900/30 hover:border-cyan-500/50 border border-gray-600 rounded-lg p-3 transition-all group"
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-bold text-gray-200 group-hover:text-cyan-400">{cat.name}</span>
                                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded group-hover:bg-cyan-900/50 group-hover:text-cyan-300">
                                        {startTime > 0 ? new Date(startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'TBD'}
                                    </span>
                                </div>
                                {(cat.duration > 0 || cat.distance) && (
                                    <div className="text-xs text-gray-400 mt-1 flex gap-3">
                                        {cat.distance && <span>{cat.distance} mi</span>}
                                        {cat.duration > 0 && <span>{Math.round(cat.duration / 60000)} mins</span>}
                                        <span>{cat.athletes?.length || 0} Athletes</span>
                                    </div>
                                )}
                            </Link>
                        )})}
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default CategorySelectionModal;
